<template>
  <div v-if="!loading" class="col-12 white-bg m2">
    <div class="flex justify-center p2">
      <div class="center">
        <p class="mb2">
          Une commande est déjà en cours, souhaitez-vous la reprendre ?
        </p>

        <p class="bold">Détails:</p>
        <p v-if="session">
          {{ label }} du {{ sessionDate }} à {{ sessionStart }}
        </p>
        <p v-else>{{ label }}</p>
      </div>
    </div>
    <div class="col-12 flex justify-between p2">
      <Button
        class="col-3"
        text="Retour"
        color="grey"
        text-size="h7"
        text-color="black"
        :text-caps="false"
        :action="() => backToAlpha()"
      />
      <Button
        v-if="!excludeResumeOrder"
        class="col-3"
        text="Reprendre ma dernière commande"
        color="red-bg"
        padding=""
        text-size="h7"
        text-color="white"
        :text-bold="false"
        :text-caps="false"
        :action="() => resumeLastPurchase()"
      />
      <Button
        class="col-3"
        text="Continuer ma nouvelle commande"
        color="red-bg"
        padding=""
        text-size="h7"
        text-color="white"
        :text-bold="false"
        :text-caps="false"
        :action="() => makeNewPurchase()"
      />
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns';

import {
  changeToPage,
  computeTariffOptions,
  computeAvailableOptions,
  initializeCartItem
} from '@/utils/logics';
import { backToAlpha, frenchTime } from '@/utils/utils';
import { removeShoppingCart, cancelPreReservedSessions } from '@/utils/requests';

import Button from '@/components/elements/Button.vue';

export default {
  components: { Button },
  data () {
    return { cartItem: null };
  },
  computed: {
    siteInfo () {
      return this.$store.state.siteInfo;
    },
    loading () {
      return this.$store.state.loading;
    },
    newTDA () {
      return this.$store.state.newTDA;
    },
    subscriptionTDA () {
      return this.$store.state.subscriptionTDA;
    },
    offerInfo () {
      return this.$store.state.offerInfo;
    },
    options () {
      return this.offerInfo.options;
    },
    tariffs () {
      return this.offerInfo.detail.prices.map(tariff => ({
        ...tariff,
        availableOptions: computeTariffOptions(
          computeAvailableOptions(this.offerInfo.options, tariff).filter(
            option => option.price
          ),
          tariff
        )
      }));
    },
    cartRecap () {
      if (this.subscriptionTDA) {
        return 'NewTDACartRecapRecurring';
      }
      return `${this.newTDA ? 'NewTDA' : ''}CartRecap`;
    },
    workspace () {
      return this.$store.getters.workspace;
    },
    shoppingCart () {
      return (this.$store.state.user?.shoppingCart || []).find(
        item => item.workspace === this.workspace
      );
    },
    page () {
      return this.$store.state.page;
    },
    user () {
      return this.$store.state.user;
    },
    product () {
      return this.$store.state.product;
    },
    preReservationOrder () {
      return this.$store.state.preReservationOrder;
    },
    paymentLink () {
      return this.preReservationOrder?.orderGeneric?.order?.payment_link_url;
    },
    cartProduct () {
      if (this.$store.getters.isEscapeGame) {
        return this.shoppingCart.details[0];
      }

      return this.shoppingCart?.details.find(d => {
        if (this.siteInfo && this.siteInfo.is_internal_session) {
          return d.horanet_product_code === this.offerInfo.uuid;
        }
        return parseInt(d.horanet_product_code) === this.offerInfo.horanet_product.horanet_product_code;
      });
    },
    session () {
      if (this.preReservationOrder) {
        return this.preReservationOrder.orderGeneric?.order?.order_items?.[0]?.session;
      }
      return this.cartProduct?.session;
    },
    label () {
      return this.session
        ? this.session.name
        : this.cartProduct?.product_name;
    },
    sessionDate () {
      return this.session && this.session.start_time
        ? frenchTime(parseInt(this.session.start_time), 'dd-MM-yyyy')
        : null;
    },
    sessionStart () {
      return this.session && this.session.start_time
        ? frenchTime(
          parseInt(this.session.start_time),
          'HH:mm'
        )
        : null;
    },
    excludeResumeOrder () {
      return !!this.$store.state.preReservationOrder?.excludeResumeOrder;
    }
  },
  methods: {
    changeToPage,
    backToAlpha,
    resumeLastPurchase () {
      if (this.preReservationOrder && !this.preReservationOrder.alreadyInCart && this.paymentLink) {
        window.location.href = this.paymentLink;
        return;
      }

      this.cartItem = JSON.parse(
        JSON.stringify(
          this.$store.state.user?.shoppingCart?.find(
            item => item.workspace === this.workspace
          )
        )
      );
      this.$store.commit(
        'addCartItemToCart',
        this.$store.state.user?.shoppingCart
      );

      if (this.newTDA) {
        const tariffs = [];

        this.cartItem.details.forEach(detail => {
          const tariff = this.tariffs.find(
            t => t.tariffCode === detail.tariffCode
          );

          tariff.options = [];

          detail.options.forEach(option => {
            const detailOption = tariff.availableOptions.find(
              opt => opt.price.tariffCode === option.tariffCode
            );
            tariff.options.push({ ...detailOption, quantity: option.quantity });
          });

          tariffs.push({ ...tariff, quantity: detail.quantity });
        });

        const participants = [];
        this.cartItem.details.forEach(detail => {
          const { __typename, ...participant } = detail.participant;
          if (!participants.some(p => p.uuid === participant.uuid)) {
            participants.push(participant);
          }
        });

        initializeCartItem({
          participants,
          offer: this.offerInfo,
          product: this.product,
          callback: () => {
            const fieldsToAdd = { tariffs: [...tariffs] };
            if (this.session) {
              fieldsToAdd.session = {
                ...this.session,
                startTime: format(parseInt(this.session.start_time), 'T'),
                endTime: format(parseInt(this.session.end_time), 'T'),
                lastTime: format(parseInt(this.session.last_time), 'T')
              };
            }
            this.$store.commit('setCartItem', {
              ...this.$store.state.cartItem,
              ...fieldsToAdd
            });

            this.changeToPage(this.cartRecap);
          },
          resumeOrder: true
        });
      } else {
        this.$store.commit('setCartItem', {
          workspace: this.cartItem.workspace,
          participants: this.cartItem.details.map(detail => {
            const { __typename, ...participant } = detail.participant;
            return {
              ...participant,
              quantity: detail.quantity,
              tariff: { price: detail.price },
              options: detail.options
            };
          })
        });

        this.changeToPage(this.cartRecap);
      }
    },
    async makeNewPurchase () {
      this.$store.commit('setLoading', true);
      this.$store.commit('clearVisitedSteps');

      if (this.preReservationOrder && !this.preReservationOrder.alreadyInCart) {
        const cancelResponse = await cancelPreReservedSessions(this.preReservationOrder.orderGeneric?.uuid);

        if (!cancelResponse || !cancelResponse.success) {
          this.$store.commit('setError', 'Erreur lors de l\'annulation de la pre-reserve');
          this.$store.commit('setLoading', false);
          return;
        }

        const updatedUser = JSON.parse(JSON.stringify(this.user));
        const orderIndex = updatedUser.orders.findIndex(
          o => o.uuid === this.preReservationOrder.orderGeneric?.uuid
        );
        if (orderIndex !== -1) {
          updatedUser.orders[orderIndex].cancelledPreReservations = true;
        }
        this.$store.commit('setUser', updatedUser);

        this.$store.commit('setPreReservationOrder', null);
      } else {
        await removeShoppingCart().then(res => {
          if (res) {
            this.$store.commit('setUser', { ...this.user, shoppingCart: res });
          }
        });
      }

      this.changeToPage(this.page);
      this.$store.commit('setLoading', false);
    }
  }
};
</script>

<style scoped>
</style>
